import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useOfferDashboard } from "../context/OfferDashboardContext";
import { useActiveTheme } from "../context/ThemeContext";
import { MetricCard } from "../components";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";
import { MultiSelectDropdown } from "../components/FilterBar";
import { ResponsivePie } from "@nivo/pie";
import { DollarSign, Users, PhoneCall, Award, TrendingDown, TrendingUp, Plus, Trash2, Edit2, Settings, X, PlusCircle, History, EyeOff, Eye } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

interface FinancialSummary {
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  totalExpenses: number;
  netProfit: number;
  callsBooked: number;
  showRate: number;
  closeRate: number;
  avgDealSize: number;
}
interface RevenueDay { date: string; takeHome: number; cash: number; }
interface ExpenseDay { date: string; total: number; }
interface PieData { name: string; value: number; }
interface AffiliatePayoutDetail {
  source: "Whop" | "Commas";
  date: string;
  type: string;
  company: string;
  gross: number;
  fees: number;
  net: number;
}
interface ExpenseRecord {
  _id: string;
  date: string;
  category: string;
  description: string;
  amount: number;
  offer: string;
  recurring: boolean;
  source?: string;
  readOnly?: boolean;
}
interface TakeHomeThreshold { amount: number; percentage: number; }
interface TakeHomePeriod { startDate: string; endDate: string; percentage: number; }
interface TakeHomeConfigItem { offer: string; percentage: number; thresholds: TakeHomeThreshold[]; periods: TakeHomePeriod[]; closerPct: number; setterPct: number; }
interface CloserBonusRecord { _id: string; offer: string; amount: number; date: string; description: string; }
interface OfferPnl {
  offer: string;
  cashCollected: number;
  companyRevenue?: number;
  adSpend?: number;
  refunds?: number;
  cashAfterRefunds?: number;
  processingFees?: number | null;
  feesAlreadyNetted?: boolean;
  salesManagerCommission?: number;
  salesManagerLabel?: string;
  closerCommission?: number;
  setterPayout?: number;
  affiliateCommissionPayout?: number;
  affiliateRetainerPayout?: number;
  affiliatePayout?: number;
  totalPayoutLiability?: number;
  netAfterFeesAndPayouts?: number;
  netAfterFees?: number;
  ownerRate: number;
  ownerTakeHome: number;
  formula: string;
  source: string;
}

function fmt$(n: number) { if (n >= 1000000) return '$' + (n / 1000000).toFixed(2) + 'M'; if (n >= 1000) return '$' + (n / 1000).toFixed(1) + 'K'; return '$' + n.toLocaleString(); }
function fmtFull$(n: number) { return '$' + Math.round(n).toLocaleString(); }

// ── Nivo helpers ──
type NivoDatum = { id: string; label: string; value: number; color: string; percentage: number };

const toNivoPieData = (data: PieData[], colors: string[]): NivoDatum[] => {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((s, d) => s + d.value, 0);
  return sorted.map((item, i) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[i % colors.length] ?? '#888',
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

function NivoPieTooltip({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) {
  return (
    <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: datum.color }} />
        <span className="font-medium">{datum.id}</span>
      </div>
      <div className="mt-1 text-muted-foreground">
        {fmtFull$(datum.value)} ({datum.data.percentage.toFixed(1)}%)
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ color?: string; name?: string; value?: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold" style={{ color: p.color || 'var(--brand-primary)' }}>
          {p.name}: {typeof p.value === 'number' ? fmtFull$(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

function StackedTooltip({ active, payload, label, offers }: { active?: boolean; payload?: Array<{ color?: string; name?: string; value?: number; payload?: Record<string, unknown> }>; label?: string; offers: string[] }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const total = row ? offers.reduce((sum, o) => sum + (Number(row[o]) || 0), 0) : 0;
  return (
    <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload.filter(p => Number(row?.[p.name || ''] || 0) > 0).map((p, i) => {
        const raw = Number(row?.[p.name || ''] || 0);
        const pct = total > 0 ? (raw / total * 100).toFixed(1) : '0';
        return (
          <p key={i} className="font-semibold" style={{ color: p.color }}>
            {p.name}: {fmtFull$(raw)} ({pct}%)
          </p>
        );
      })}
    </div>
  );
}

// ── Take-Home / Payout Config Dialog ──
type ConfigDialogMode = 'takeHome' | 'closerPayout' | 'setterPayout';

function PayoutConfigDialog({ open, onClose, mode }: { open: boolean; onClose: () => void; mode: ConfigDialogMode }) {
  const offerDashboard = useOfferDashboard();
  const [configs, setConfigs] = useState<TakeHomeConfigItem[]>([]);
  const [allOffers, setAllOffers] = useState<string[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [bonusesByOffer, setBonusesByOffer] = useState<Record<string, CloserBonusRecord[]>>({});
  const [showBonusHistory, setShowBonusHistory] = useState<Record<string, boolean>>({});
  const [newBonus, setNewBonus] = useState<Record<string, { amount: string; date: string; description: string }>>({});

  const withDashboard = useCallback((path: string) => {
    if (!offerDashboard.isEmbeddedMode || !offerDashboard.dashboardSlug) {
      return path;
    }
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}dashboard=${encodeURIComponent(offerDashboard.dashboardSlug)}`;
  }, [offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      fetch(withDashboard("/api/take-home-config")).then(r => r.json()),
      fetch(withDashboard("/api/take-home-config/offers")).then(r => r.json()).catch(() => ({ success: false })),
    ]).then(([configData, offerData]) => {
      if (configData.success) setConfigs(configData.configs.map((c: Record<string, unknown>) => ({
        offer: c.offer as string, percentage: c.percentage as number,
        thresholds: (c.thresholds || []) as TakeHomeThreshold[],
        periods: ((c.periods || []) as Array<Record<string, unknown>>).map(p => ({
          startDate: p.startDate ? new Date(p.startDate as string).toISOString().split("T")[0] : "",
          endDate: p.endDate ? new Date(p.endDate as string).toISOString().split("T")[0] : "",
          percentage: p.percentage as number,
        })),
        closerPct: (c.closerPct as number) ?? 10, setterPct: (c.setterPct as number) ?? 5,
      })));
      const offers: string[] = [];
      if (offerData.success && Array.isArray(offerData.offers)) offers.push(...offerData.offers);
      if (configData.success) { for (const c of configData.configs as TakeHomeConfigItem[]) { if (!offers.includes(c.offer)) offers.push(c.offer); } }
      setAllOffers(offers.filter(Boolean));
    });
    if (mode === 'closerPayout' || mode === 'setterPayout') {
      const endpoint = mode === 'closerPayout' ? '/api/closer-bonuses' : '/api/setter-bonuses';
      fetch(withDashboard(endpoint)).then(r => r.json()).then(data => {
        if (data.success) {
          const grouped: Record<string, CloserBonusRecord[]> = {};
          for (const b of data.bonuses as CloserBonusRecord[]) { if (!grouped[b.offer]) grouped[b.offer] = []; grouped[b.offer]!.push(b); }
          setBonusesByOffer(grouped);
        }
      });
    }
  }, [open, mode, withDashboard]);

  const getConfig = (offer: string): TakeHomeConfigItem =>
    configs.find(c => c.offer === offer) || { offer, percentage: 30, thresholds: [], periods: [], closerPct: 10, setterPct: 5 };

  const updateConfig = (offer: string, updates: Partial<TakeHomeConfigItem>) => {
    setConfigs(prev => {
      const idx = prev.findIndex(c => c.offer === offer);
      if (idx >= 0) { const updated = [...prev]; updated[idx] = { ...updated[idx], ...updates } as TakeHomeConfigItem; return updated; }
      return [...prev, { offer, percentage: 30, thresholds: [] as TakeHomeThreshold[], periods: [] as TakeHomePeriod[], closerPct: 10, setterPct: 5, ...updates } as TakeHomeConfigItem];
    });
  };

  const saveOffer = async (offer: string) => {
    setSaving(offer);
    const cfg = getConfig(offer);
    await fetch(withDashboard("/api/take-home-config"), { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ offer: cfg.offer, percentage: cfg.percentage, periods: cfg.periods.filter(p => p.startDate), closerPct: cfg.closerPct, setterPct: cfg.setterPct }) });
    setSaving(null);
  };

  const addPeriod = (offer: string) => {
    const cfg = getConfig(offer);
    updateConfig(offer, { periods: [...cfg.periods, { startDate: new Date().toISOString().split("T")[0] || "", endDate: "", percentage: 30 }] });
  };
  const removePeriod = (offer: string, idx: number) => { const cfg = getConfig(offer); updateConfig(offer, { periods: cfg.periods.filter((_, i) => i !== idx) }); };
  const updatePeriod = (offer: string, idx: number, field: keyof TakeHomePeriod, value: string | number) => {
    const cfg = getConfig(offer); const periods = [...cfg.periods]; const existing = periods[idx];
    if (existing) periods[idx] = { ...existing, [field]: value }; updateConfig(offer, { periods });
  };

  const bonusEndpoint = mode === 'closerPayout' ? '/api/closer-bonuses' : '/api/setter-bonuses';
  const addBonus = async (offer: string) => {
    const b = newBonus[offer]; if (!b?.amount || !b?.date) return;
    const res = await fetch(withDashboard(bonusEndpoint), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ offer, amount: Number(b.amount), date: b.date, description: b.description || "" }) });
    const data = await res.json();
    if (data.success) { setBonusesByOffer(prev => ({ ...prev, [offer]: [data.bonus, ...(prev[offer] || [])] })); setNewBonus(prev => ({ ...prev, [offer]: { amount: "", date: "", description: "" } })); }
  };
  const deleteBonus = async (offer: string, id: string) => {
    const res = await fetch(withDashboard(`${bonusEndpoint}/${id}`), { method: "DELETE" });
    if ((await res.json()).success) setBonusesByOffer(prev => ({ ...prev, [offer]: (prev[offer] || []).filter(b => b._id !== id) }));
  };

  if (!open) return null;
  const titles: Record<ConfigDialogMode, { title: string; subtitle: string }> = {
    takeHome: { title: 'Take-Home Configuration', subtitle: 'Set per-offer take-home percentages with date-based periods' },
    closerPayout: { title: 'Closer Payout Configuration', subtitle: 'Set closer payout percentage and bonuses per offer' },
    setterPayout: { title: 'Setter Payout Configuration', subtitle: 'Set setter payout percentage and bonuses per offer' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-[600px] max-h-[80vh] overflow-auto bg-card border border-border rounded-2xl p-6 shadow-2xl z-10"
      >
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-lg font-bold text-foreground">{titles[mode].title}</h2>
            <p className="text-xs text-muted-foreground mt-1">{titles[mode].subtitle}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1"><X size={20} /></button>
        </div>
        {allOffers.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-5">No offers found. Data will appear once EOC records are synced.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {allOffers.map(offer => {
              const cfg = getConfig(offer);
              const offerBonuses = bonusesByOffer[offer] || [];
              const nb = newBonus[offer] || { amount: "", date: "", description: "" };
              return (
                <div key={offer} className="border border-border rounded-xl p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-foreground">{offer}</h3>
                    <button onClick={() => saveOffer(offer)} disabled={saving === offer}
                      className="px-3 py-1 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] disabled:opacity-60">
                      {saving === offer ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {mode === 'takeHome' && (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <label className="text-xs font-medium text-muted-foreground min-w-[90px]">Default %:</label>
                        <input type="number" min={0} max={100} value={cfg.percentage} onChange={e => updateConfig(offer, { percentage: Number(e.target.value) })}
                          className="w-16 px-2 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground focus:outline-none" />
                        <span className="text-xs text-muted-foreground">% (fallback)</span>
                      </div>
                      {cfg.periods.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Date Periods</p>
                          {cfg.periods.map((p, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                              <input type="date" value={p.startDate} onChange={e => updatePeriod(offer, idx, 'startDate', e.target.value)} onClick={e => (e.target as HTMLInputElement).showPicker()}
                                className="w-32 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground cursor-pointer" />
                              <span className="text-muted-foreground text-xs">→</span>
                              <input type="date" value={p.endDate} onChange={e => updatePeriod(offer, idx, 'endDate', e.target.value)} onClick={e => (e.target as HTMLInputElement).showPicker()}
                                className="w-32 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground cursor-pointer" />
                              <input type="number" min={0} max={100} value={p.percentage} onChange={e => updatePeriod(offer, idx, 'percentage', Number(e.target.value))}
                                className="w-14 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground" />
                              <span className="text-xs text-muted-foreground">%</span>
                              <button onClick={() => removePeriod(offer, idx)} className="text-red-500 p-0.5"><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={() => addPeriod(offer)} className="flex items-center gap-1 px-2 py-1 mt-1 rounded-lg border border-dashed border-border text-muted-foreground hover:text-foreground text-xs transition-colors">
                        <PlusCircle size={12} /> Add Period
                      </button>
                    </>
                  )}
                  {(mode === 'closerPayout' || mode === 'setterPayout') && (
                    <>
                      <div className="flex items-center gap-3 mb-3">
                        <label className="text-xs font-medium text-muted-foreground min-w-[90px]">{mode === 'closerPayout' ? 'Closer' : 'Setter'} %:</label>
                        <input type="number" min={0} max={100} value={mode === 'closerPayout' ? cfg.closerPct : cfg.setterPct}
                          onChange={e => updateConfig(offer, mode === 'closerPayout' ? { closerPct: Number(e.target.value) } : { setterPct: Number(e.target.value) })}
                          className="w-16 px-2 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground" />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                      <div className="border-t border-border pt-2.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">Add Bonus</p>
                        <div className="flex gap-1.5 items-center flex-wrap">
                          <input type="number" placeholder="$" value={nb.amount} onChange={e => setNewBonus(prev => ({ ...prev, [offer]: { ...nb, amount: e.target.value } }))}
                            className="w-20 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground" />
                          <input type="date" value={nb.date} onChange={e => setNewBonus(prev => ({ ...prev, [offer]: { ...nb, date: e.target.value } }))} onClick={e => (e.target as HTMLInputElement).showPicker()}
                            className="w-32 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground cursor-pointer" />
                          <input type="text" placeholder="Description" value={nb.description} onChange={e => setNewBonus(prev => ({ ...prev, [offer]: { ...nb, description: e.target.value } }))}
                            className="flex-1 min-w-24 px-1.5 py-1 text-xs rounded-lg border border-border bg-transparent text-foreground" />
                          <button onClick={() => addBonus(offer)} disabled={!nb.amount || !nb.date}
                            className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-emerald-500 disabled:opacity-50">Add</button>
                        </div>
                      </div>
                      {offerBonuses.length > 0 && (
                        <div className="mt-2">
                          <button onClick={() => setShowBonusHistory(prev => ({ ...prev, [offer]: !prev[offer] }))}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                            <History size={12} /> {showBonusHistory[offer] ? 'Hide' : 'Show'} Bonus History ({offerBonuses.length})
                          </button>
                          {showBonusHistory[offer] && (
                            <div className="mt-1.5">
                              {offerBonuses.map(b => (
                                <div key={b._id} className="flex items-center gap-2 py-1 border-b border-border text-xs">
                                  <span className="text-muted-foreground min-w-[70px]">{new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  <span className="text-emerald-400 font-semibold min-w-[60px]">${b.amount.toLocaleString()}</span>
                                  <span className="text-foreground flex-1">{b.description}</span>
                                  <button onClick={() => deleteBonus(offer, b._id)} className="text-muted-foreground hover:text-red-500 p-0.5"><Trash2 size={12} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function OtherOffersPopup({ open, onClose, offers }: { open: boolean; onClose: () => void; offers: PieData[] }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-[480px] max-h-[70vh] overflow-auto bg-card border border-border rounded-2xl p-6 shadow-2xl z-10"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-bold text-foreground">All Offers Breakdown</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1"><X size={20} /></button>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {['#', 'Offer', 'Take-Home'].map(h => (
                <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase border-b border-border">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...offers].sort((a, b) => b.value - a.value).map((o, i) => (
              <tr key={i} className="border-b border-border hover:bg-secondary/30 transition-colors">
                <td className="py-2 px-3 text-xs text-muted-foreground font-semibold">{i + 1}</td>
                <td className="py-2 px-3 text-sm text-foreground font-medium">{o.name}</td>
                <td className="py-2 px-3 text-sm font-semibold text-right text-[var(--brand-primary)]">{fmtFull$(o.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 pt-2 border-t border-border flex justify-between">
          <span className="text-xs font-semibold text-muted-foreground">Total</span>
          <span className="text-sm font-bold text-foreground">{fmtFull$(offers.reduce((sum, o) => sum + o.value, 0))}</span>
        </div>
      </motion.div>
    </div>
  );
}

export function Financials() {
  const { formatForApi } = useDateRange();
  const { offer } = useFilters();
  const offerDashboard = useOfferDashboard();
  const activeTheme = useActiveTheme();
  const CHART_COLORS = activeTheme.colors.chart;

  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueDay[]>([]);
  const [expensesData, setExpensesData] = useState<ExpenseDay[]>([]);
  const [expensesByCategory, setExpensesByCategory] = useState<PieData[]>([]);
  const [incomeByOffer, setIncomeByOffer] = useState<PieData[]>([]);
  const [stackedAreaData, setStackedAreaData] = useState<Record<string, unknown>[]>([]);
  const [stackedOffers, setStackedOffers] = useState<string[]>([]);
  const [offerPnl, setOfferPnl] = useState<OfferPnl[]>([]);
  const [ownerTakeHome, setOwnerTakeHome] = useState(0);
  const [companyRevenue, setCompanyRevenue] = useState(0);
  const [affiliatePayouts, setAffiliatePayouts] = useState(0);
  const [affiliateDetails, setAffiliateDetails] = useState<AffiliatePayoutDetail[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [editingExpense, setEditingExpense] = useState<ExpenseRecord | null>(null);
  const [deletingExpense, setDeletingExpense] = useState<ExpenseRecord | null>(null);
  const [addingExpense, setAddingExpense] = useState(false);
  const [configDialogMode, setConfigDialogMode] = useState<ConfigDialogMode | null>(null);
  const [otherOffersOpen, setOtherOffersOpen] = useState(false);
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [availableOffers, setAvailableOffers] = useState<string[]>([]);
  const [showExpenses, setShowExpenses] = useState(() => {
    try { return localStorage.getItem("financials_showExpenses") !== "false"; } catch { return true; }
  });

  const withDashboard = useCallback((path: string) => {
    if (!offerDashboard.isEmbeddedMode || !offerDashboard.dashboardSlug) {
      return path;
    }
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}dashboard=${encodeURIComponent(offerDashboard.dashboardSlug)}`;
  }, [offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  const fetchAvailableOffers = useCallback(async () => {
    try {
      const res = await fetch(withDashboard("/api/take-home-config/offers"));
      const data = await res.json();
      if (data.success && Array.isArray(data.offers)) {
        setAvailableOffers(data.offers.filter(Boolean).sort());
      }
    } catch { }
  }, [withDashboard]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(withDashboard("/api/expense-categories"));
      const data = await res.json();
      if (data.success) setCategories(data.categories.map((c: { name: string }) => c.name));
    } catch { }
  }, [withDashboard]);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      const dates = formatForApi();
      const params = new URLSearchParams({ startDate: dates.startDate, endDate: dates.endDate });
      if (selectedOffers.length > 0) {
        params.set("offer", selectedOffers.join(","));
      } else if (offer && offer !== "all") {
        params.set("offer", offer);
      }
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }
      const [res, pnlRes, affiliateRes] = await Promise.all([
        fetch(`/api/financials/summary?${params.toString()}`),
        fetch(`/api/financials/offer-pnl?startDate=${encodeURIComponent(dates.startDate)}&endDate=${encodeURIComponent(dates.endDate)}`),
        fetch(`/api/financials/affiliate-payouts?startDate=${encodeURIComponent(dates.startDate)}&endDate=${encodeURIComponent(dates.endDate)}`),
      ]);
      const data = await res.json();
      const pnlData = await pnlRes.json();
      const affiliateData = await affiliateRes.json();
      setAffiliatePayouts(Number(affiliateData?.data?.net || 0));
      setAffiliateDetails(Array.isArray(affiliateData?.data?.details) ? affiliateData.data.details : []);
      if (pnlData.success) {
        setOfferPnl(Array.isArray(pnlData.offers) ? pnlData.offers : []);
        setOwnerTakeHome(Number(pnlData.totals?.takeHome || 0));
        setCompanyRevenue(Number(pnlData.totals?.companyRevenue || 0));
      } else {
        setOfferPnl([]);
        setOwnerTakeHome(0);
        setCompanyRevenue(0);
      }
      if (data.success) {
        setSummary({
          cashCollected: data.summary.cashCollected || 0,
          closerPayouts: data.summary.closerPayouts || 0,
          setterPayouts: data.summary.setterPayouts || 0,
          takeHome: data.summary.takeHome || 0,
          totalExpenses: data.summary.totalExpenses || 0,
          netProfit: data.summary.netProfit || 0,
          callsBooked: data.summary.callsBooked || 0,
          showRate: data.summary.showRate || 0,
          closeRate: data.summary.closeRate || 0,
          avgDealSize: data.summary.avgDealSize || 0,
        });
        setRevenueData(data.charts.revenueByDay || []);
        setExpensesData((data.charts.expensesByDay || []).map((e: { date: string; total: number }) => ({ date: e.date, total: typeof e.total === 'number' ? e.total : 0 })));
        setExpensesByCategory((data.charts.expensesByCategory || []).map((e: { category: string; amount: number }) => ({ name: e.category, value: e.amount })));
        setIncomeByOffer((data.charts.incomeByOffer || []).map((e: { offer: string; amount: number }) => ({ name: e.offer, value: e.amount })));

        const rawStacked: Record<string, unknown>[] = data.charts.revenueByOfferStacked || [];
        const allOffers: string[] = data.charts.stackedOffers || [];
        const offerTotals = new Map<string, number>();
        for (const o of allOffers) offerTotals.set(o, 0);
        for (const row of rawStacked) { for (const o of allOffers) offerTotals.set(o, (offerTotals.get(o) || 0) + ((row[o] as number) || 0)); }
        const sorted = Array.from(offerTotals.entries()).sort((a, b) => b[1] - a[1]);
        const topOffers = sorted.filter(([, v]) => v > 0).slice(0, 8).map(([name]) => name);
        const otherOffers = allOffers.filter(o => !topOffers.includes(o));
        const hasOther = otherOffers.length > 0;
        const finalOffers = hasOther ? [...topOffers, 'Other'] : topOffers;
        const weekMap = new Map<string, Record<string, unknown>>();
        for (let i = 0; i < rawStacked.length; i++) {
          const row = rawStacked[i]; const weekKey = `week-${Math.floor(i / 7)}`;
          if (!weekMap.has(weekKey)) weekMap.set(weekKey, { date: row.date as string });
          const bucket = weekMap.get(weekKey)!;
          for (const o of topOffers) bucket[o] = ((bucket[o] as number) || 0) + ((row[o] as number) || 0);
          if (hasOther) { let otherSum = 0; for (const o of otherOffers) otherSum += (row[o] as number) || 0; bucket['Other'] = ((bucket['Other'] as number) || 0) + otherSum; }
        }
        const sortedFinal = finalOffers.sort((a, b) => {
          const aT = a === 'Other' ? sorted.slice(8).reduce((s, [, v]) => s + v, 0) : (offerTotals.get(a) || 0);
          const bT = b === 'Other' ? sorted.slice(8).reduce((s, [, v]) => s + v, 0) : (offerTotals.get(b) || 0);
          return bT - aT;
        });
        setStackedAreaData(Array.from(weekMap.values()));
        setStackedOffers(sortedFinal);
      }
    } catch { } finally { setIsLoading(false); }
  }, [formatForApi, offer, selectedOffers, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  const fetchExpenses = useCallback(async () => {
    try {
      const dates = formatForApi();
      const params = new URLSearchParams({ startDate: dates.startDate, endDate: dates.endDate, limit: "100" });
      if (selectedOffers.length > 0) {
        params.set("offer", selectedOffers.join(","));
      } else if (offer && offer !== "all") {
        params.set("offer", offer);
      }
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }
      const res = await fetch(`/api/expenses?${params.toString()}`);
      const data = await res.json();
      if (data.success) setExpenses(data.expenses);
    } catch { }
  }, [formatForApi, offer, selectedOffers, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchAvailableOffers(); }, [fetchAvailableOffers]);

  const handleAddCategory = async (name: string): Promise<boolean> => {
    if (!name.trim()) return false;
    const res = await fetch(withDashboard("/api/expense-categories"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: name.trim() }) });
    const data = await res.json();
    if (data.success) { await fetchCategories(); return true; }
    return false;
  };

  const expenseFields: FieldDefinition[] = [
    { key: "date", label: "Date", type: "date", required: true },
    { key: "offer", label: "Attributed To", type: "select", required: true, options: [{ value: "All Offers", label: "All Offers (shared overhead)" }, ...availableOffers.map(o => ({ value: o, label: o }))] },
    { key: "category", label: "Category", type: "select", required: true, options: (categories.length > 0 ? categories : ["Salary", "Software/Tech Stack", "Ads", "Contractor", "Office", "Other"]).map(c => ({ value: c, label: c })), onAddOption: handleAddCategory },
    { key: "description", label: "Description", type: "text", required: true, placeholder: "Enter description" },
    { key: "amount", label: "Amount ($)", type: "number", required: true, placeholder: "0" },
    { key: "recurring", label: "Recurring", type: "select", options: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }] },
  ];

  const handleAddExpense = async (data: Record<string, unknown>) => {
    const res = await fetch(withDashboard("/api/expenses"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ date: data.date, offer: data.offer, category: data.category, description: data.description, amount: Number(data.amount), recurring: data.recurring === "true" }) });
    if ((await res.json()).success) { setAddingExpense(false); fetchExpenses(); fetchSummary(); }
  };
  const handleEditExpense = async (data: Record<string, unknown>) => {
    if (!editingExpense || editingExpense.readOnly) return;
    const res = await fetch(withDashboard(`/api/expenses/${editingExpense._id}`), { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ date: data.date, offer: data.offer, category: data.category, description: data.description, amount: Number(data.amount), recurring: data.recurring === "true" }) });
    if ((await res.json()).success) { setEditingExpense(null); fetchExpenses(); fetchSummary(); }
  };
  const handleDeleteExpense = async () => {
    if (!deletingExpense) return;
    const res = await fetch(withDashboard(`/api/expenses/${deletingExpense._id}`), { method: "DELETE" });
    if ((await res.json()).success) { setDeletingExpense(null); fetchExpenses(); fetchSummary(); }
  };

  const getEditData = (expense: ExpenseRecord) => ({ ...expense, date: new Date(expense.date).toISOString().split("T")[0], recurring: String(expense.recurring) });
  const getNewExpenseData = () => ({ _id: "", date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0], offer: "All Offers", category: "Other", description: "", amount: 0, recurring: "false" });

  const cashCollected = summary?.cashCollected || 0;
  const closerPayouts = summary?.closerPayouts || 0;
  const setterPayouts = summary?.setterPayouts || 0;
  const teamPayouts = closerPayouts + setterPayouts;
  // The summary endpoint is the selected-period InfoScalers expense ledger.
  // Cr8Her processor fees are already deducted by the live Cr8Her Payouts engine
  // before its COO bottom-line payout is included in owner take-home.
  const totalExpenses = summary?.totalExpenses || 0;
  const takeHome = ownerTakeHome;
  const companyTakeHome = companyRevenue;
  const netProfit = takeHome - totalExpenses;
  const ownerMargin = companyRevenue > 0 ? (netProfit / companyRevenue) * 100 : null;
  const isProfit = netProfit >= 0;
  const netMargin = cashCollected > 0 ? (netProfit / cashCollected) * 100 : 0;
  const salaryExpenseItems = expenses.filter((expense) => expense.category === 'Salary');
  const processorFeeItems = offerPnl.filter((item) => typeof item.processingFees === 'number' && item.processingFees > 0);
  const modalCashDistribution = offerPnl.filter((item) => item.cashCollected !== 0);
  const offerCashCollectedTotal = modalCashDistribution.reduce((total, item) => total + item.cashCollected, 0);
  const affiliateDistribution = Array.from(affiliateDetails.reduce((groups, item) => {
    const key = `${item.source}::${item.company || 'Unattributed'}`;
    const current = groups.get(key) || { source: item.source, company: item.company || 'Unattributed', gross: 0, fees: 0, net: 0, count: 0 };
    current.gross += Number(item.gross || 0); current.fees += Number(item.fees || 0); current.net += Number(item.net || 0); current.count += 1;
    groups.set(key, current); return groups;
  }, new Map<string, { source: string; company: string; gross: number; fees: number; net: number; count: number }>()).values()).sort((a, b) => b.net - a.net);
  const affiliateDistributionTotal = affiliateDistribution.reduce((total, item) => total + item.net, 0);

  // Sparkline data from revenue history
  const takeHomeSpark = revenueData.slice(-30).map(d => d.takeHome);
  const cashSpark = revenueData.slice(-30).map(d => d.cash);
  const expenseSpark = expensesData.slice(-30).map(d => d.total);

  // Nivo pie data
  const expensePieData = toNivoPieData(expensesByCategory, CHART_COLORS);
  const incomePieData = toNivoPieData(
    (() => {
      const sorted = [...incomeByOffer].sort((a, b) => b.value - a.value);
      const top7 = sorted.slice(0, 7);
      const rest = sorted.slice(7);
      const otherTotal = rest.reduce((sum, o) => sum + o.value, 0);
      return otherTotal > 0 ? [...top7, { name: 'Other', value: otherTotal }] : top7;
    })(),
    CHART_COLORS
  );

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Financials</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Revenue, expenses &amp; profit</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {availableOffers.length > 0 && (
            <MultiSelectDropdown
              label="All Offers"
              options={availableOffers.map(o => ({ value: o, label: o }))}
              value={selectedOffers}
              onChange={setSelectedOffers}
            />
          )}
        </div>
      </motion.div>

      {/* Owner P&L — source-of-truth payout report */}
      <section className="rounded-2xl border border-border bg-card p-5 md:p-6 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Owner P&amp;L</h2>
            <p className="text-sm text-muted-foreground">Verified take-home by offer for the selected date range</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Company Revenue</p>
            <p className="text-2xl font-bold text-foreground">{isLoading ? '—' : fmtFull$(companyRevenue)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Info Scalers billed revenue in the selected period</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Combined take-home</p>
            <p className="text-2xl font-bold text-emerald-500">{isLoading ? '—' : fmtFull$(ownerTakeHome)}</p>
            <p className={`mt-1 text-sm font-semibold ${isProfit ? 'text-emerald-500' : 'text-red-500'}`}>Net profit: {isLoading ? '—' : fmtFull$(netProfit)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Personal margin: {ownerMargin === null ? 'Unavailable' : `${ownerMargin.toFixed(1)}% of company revenue`}</p>
          </div>
        </div>
        {isLoading ? (
          <div className="h-36 rounded-xl bg-muted animate-pulse" />
        ) : offerPnl.length === 0 ? (
          <p className="text-sm text-muted-foreground">Authoritative owner P&amp;L data is unavailable for this range.</p>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {offerPnl.map((item) => (
              <article key={item.offer} onClick={item.offer === 'Affiliate Payouts' ? () => setSelectedMetric('Affiliate Payouts') : undefined} role={item.offer === 'Affiliate Payouts' ? 'button' : undefined} tabIndex={item.offer === 'Affiliate Payouts' ? 0 : undefined} onKeyDown={item.offer === 'Affiliate Payouts' ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedMetric('Affiliate Payouts'); } } : undefined} className={`rounded-xl border border-border p-4 space-y-3 bg-background/40 ${item.offer === 'Affiliate Payouts' ? 'cursor-pointer transition-colors hover:border-emerald-500/60 hover:bg-emerald-500/5' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-foreground">{item.offer}</h3>
                    <p className="text-xs text-muted-foreground">{item.source}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Your take-home</p>
                    <p className="text-xl font-bold text-emerald-500">{fmtFull$(item.ownerTakeHome)}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Personal margin: {item.companyRevenue && item.companyRevenue > 0 ? `${((item.ownerTakeHome / item.companyRevenue) * 100).toFixed(1)}% of company revenue` : 'Unavailable'}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Net after fees: {item.netAfterFees !== undefined ? fmtFull$(item.netAfterFees) : item.netAfterFeesAndPayouts !== undefined ? fmtFull$(item.netAfterFeesAndPayouts) : 'Unavailable'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {item.offer !== 'Ascend Content' && <><span className="text-muted-foreground">Revenue</span><span className="text-right font-medium">{fmtFull$(item.cashCollected)}</span></>}
                  {item.adSpend !== undefined && <><span className="text-muted-foreground">Meta ad spend</span><span className="text-right font-medium">{fmtFull$(item.adSpend)}</span></>}
                  {item.refunds !== undefined && <><span className="text-muted-foreground">Refunds</span><span className="text-right font-medium">{fmtFull$(item.refunds)}</span></>}
                  {item.cashAfterRefunds !== undefined && <><span className="text-muted-foreground">Cash after refunds</span><span className="text-right font-medium">{fmtFull$(item.cashAfterRefunds)}</span></>}
                  {item.offer !== 'Urban Unity' && item.processingFees !== undefined && <><span className="text-muted-foreground">Processor fees</span><span className="text-right font-medium">{item.processingFees === null ? 'Unavailable' : fmtFull$(item.processingFees)}</span></>}
                  {item.salesManagerCommission !== undefined && <><span className="text-muted-foreground">{item.salesManagerLabel || 'Sales manager (5% gross cash)'}</span><span className="text-right font-medium">{fmtFull$(item.salesManagerCommission)}</span></>}
                  {item.closerCommission !== undefined && <><span className="text-muted-foreground">Closer commissions</span><span className="text-right font-medium">{fmtFull$(item.closerCommission)}</span></>}
                  {item.setterPayout !== undefined && <><span className="text-muted-foreground">Setter commissions</span><span className="text-right font-medium">{fmtFull$(item.setterPayout)}</span></>}
                  {item.affiliateCommissionPayout !== undefined && <><span className="text-muted-foreground">Affiliate commissions</span><span className="text-right font-medium">{fmtFull$(item.affiliateCommissionPayout)}</span></>}
                  {item.affiliateRetainerPayout !== undefined && <><span className="text-muted-foreground">Affiliate retainers</span><span className="text-right font-medium">{fmtFull$(item.affiliateRetainerPayout)}</span></>}
                  {item.totalPayoutLiability !== undefined && <><span className="text-muted-foreground">Total sales payout liability</span><span className="text-right font-medium">{fmtFull$(item.totalPayoutLiability)}</span></>}
                  {item.netAfterFeesAndPayouts !== undefined && <><span className="text-muted-foreground">After fees &amp; sales payouts</span><span className="text-right font-medium">{fmtFull$(item.netAfterFeesAndPayouts)}</span></>}
                </div>
                <p className="text-xs text-muted-foreground border-t border-border pt-3">{item.formula}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Top metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Cash Collected"
          onClick={() => setSelectedMetric('Cash Collected')}
          value={isLoading ? '$0' : fmt$(cashCollected)}
          icon={<DollarSign className="w-5 h-5" />}
          color="brand"
          data={cashSpark}
          tooltipFormatter={v => fmt$(v)}
          index={0}
        />
        <MetricCard
          label="Closer Payouts"
          onClick={() => setSelectedMetric('Closer Payouts')}
          value={isLoading ? '$0' : fmt$(closerPayouts)}
          icon={<Users className="w-5 h-5" />}
          color="blue"
          index={1}
        />
        <MetricCard
          label="Setter Payouts"
          onClick={() => setSelectedMetric('Setter Payouts')}
          value={isLoading ? '$0' : fmt$(setterPayouts)}
          icon={<PhoneCall className="w-5 h-5" />}
          color="purple"
          index={2}
        />
        <MetricCard
          label="Team Payouts"
          onClick={() => setSelectedMetric('Team Payouts')}
          value={isLoading ? '$0' : fmt$(teamPayouts)}
          icon={<Users className="w-5 h-5" />}
          color="cyan"
          index={3}
        />
        <MetricCard
          label="Company Take Home"
          onClick={() => setSelectedMetric('Company Take Home')}
          value={isLoading ? '$0' : fmt$(companyTakeHome)}
          icon={<DollarSign className="w-5 h-5" />}
          color="green"
          index={4}
        />
        <MetricCard
          label="Affiliate Payouts"
          onClick={() => setSelectedMetric('Affiliate Payouts')}
          value={isLoading ? '$0' : fmt$(affiliatePayouts)}
          icon={<DollarSign className="w-5 h-5" />}
          color="green"
          index={5}
        />
        <MetricCard
          label="Total Expenses"
          onClick={() => setSelectedMetric('Total Expenses')}
          value={isLoading ? '$0' : fmt$(totalExpenses)}
          icon={<TrendingDown className="w-5 h-5" />}
          color="brand"
          data={expenseSpark}
          tooltipFormatter={v => fmt$(v)}
          index={6}
        />
        <MetricCard
          label="Net Profit"
          onClick={() => setSelectedMetric('Net Profit')}
          value={isLoading ? '$0' : fmt$(netProfit)}
          icon={<TrendingUp className="w-5 h-5" />}
          color={isProfit ? "green" : "brand"}
          index={6}
        />
        <MetricCard
          label="Net Margin"
          onClick={() => setSelectedMetric('Net Margin')}
          value={isLoading ? '0%' : `${netMargin.toFixed(1)}%`}
          icon={isProfit ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
          color={isProfit ? "green" : "red"}
          index={7}
        />
      </div>

      {/* Configure payouts — subtle settings row */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex flex-wrap items-center gap-3"
      >
        {[
          { label: 'Configure Take-Home', mode: 'takeHome' as ConfigDialogMode },
          { label: 'Configure Closer Payouts', mode: 'closerPayout' as ConfigDialogMode },
          { label: 'Configure Setter Payouts', mode: 'setterPayout' as ConfigDialogMode },
        ].map(({ label, mode }) => (
          <button
            key={mode}
            onClick={() => setConfigDialogMode(mode)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/40 transition-colors"
          >
            <Settings size={12} /> {label}
          </button>
        ))}
        <button
          onClick={() => { const next = !showExpenses; setShowExpenses(next); try { localStorage.setItem("financials_showExpenses", String(next)); } catch {} }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground transition-colors ml-auto"
        >
          {showExpenses ? <EyeOff size={12} /> : <Eye size={12} />}
          {showExpenses ? 'Hide Expense Details' : 'Show Expense Details'}
        </button>
      </motion.div>

      {/* Pie Charts */}
      <div className={`grid gap-6 ${showExpenses ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {showExpenses && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <h3 className="text-base font-semibold text-foreground mb-1">Expenses by Category</h3>
            <p className="text-xs text-muted-foreground mb-4">Breakdown of where money is going</p>
            {expensePieData.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">No expense data</div>
            ) : (
              <div className="flex gap-6 items-center">
                <div style={{ height: 260, width: 220 }} className="shrink-0">
                  <ResponsivePie
                    data={expensePieData}
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                    innerRadius={0.55}
                    padAngle={1}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    sortByValue={false}
                    colors={{ datum: "data.color" }}
                    borderWidth={2}
                    borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
                    enableArcLinkLabels={false}
                    enableArcLabels={false}
                    tooltip={NivoPieTooltip}
                    legends={[]}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1 max-h-[260px] overflow-y-auto">
                  {expensePieData.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-foreground truncate">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-semibold text-foreground">{fmtFull$(item.value)}</span>
                        <span className="text-xs text-muted-foreground w-10 text-right">{item.percentage.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-card rounded-2xl border border-border p-6"
        >
          <h3 className="text-base font-semibold text-foreground mb-1">Take-Home by Offer</h3>
          <p className="text-xs text-muted-foreground mb-4">Revenue share across active offers</p>
          {incomePieData.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">No take-home data</div>
          ) : (
            <div className="flex gap-6 items-center">
              <div style={{ height: 260, width: 220 }} className="shrink-0">
                <ResponsivePie
                  data={incomePieData}
                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  innerRadius={0.55}
                  padAngle={1}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  sortByValue={false}
                  colors={{ datum: "data.color" }}
                  borderWidth={2}
                  borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
                  enableArcLinkLabels={false}
                  enableArcLabels={false}
                  tooltip={NivoPieTooltip}
                  onClick={(datum) => { if (datum.label === 'Other') setOtherOffersOpen(true); }}
                  legends={[]}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 max-h-[260px] overflow-y-auto">
                {incomePieData.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0 ${item.label === 'Other' ? 'cursor-pointer hover:bg-secondary/20 rounded transition-colors' : ''}`}
                    onClick={() => { if (item.label === 'Other') setOtherOffersOpen(true); }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-foreground truncate">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-foreground">{fmtFull$(item.value)}</span>
                      <span className="text-xs text-muted-foreground w-10 text-right">{item.percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Area + Bar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-card rounded-2xl border border-border p-6"
        >
          <h3 className="text-base font-semibold text-foreground mb-1">My Take-Home Over Time</h3>
          <p className="text-xs text-muted-foreground mb-4">Daily take-home earnings</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="profG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmt$(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="takeHome" stroke="#10B981" strokeWidth={2.5} fill="url(#profG)" name="Take-Home" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {showExpenses && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <h3 className="text-base font-semibold text-foreground mb-1">Expenses Over Time</h3>
            <p className="text-xs text-muted-foreground mb-4">Daily spend trend</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={expensesData}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmt$(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="total" name="Expenses" fill="#EF4444" radius={[6, 6, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>

      {/* Cash Collected by Offer — Stacked Area */}
      {stackedAreaData.length > 0 && stackedOffers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-card rounded-2xl border border-border p-6"
        >
          <h3 className="text-base font-semibold text-foreground mb-1">Cash Collected by Offer</h3>
          <p className="text-xs text-muted-foreground mb-4">Revenue mix across offers (weekly, % share)</p>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={stackedAreaData} stackOffset="expand">
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} />
              <Tooltip content={<StackedTooltip offers={stackedOffers} />} />
              <Legend
                wrapperStyle={{ fontSize: '11px' }}
                payload={stackedOffers.slice(0, 5).map((o, i) => ({ value: o, type: 'square' as const, color: CHART_COLORS[stackedOffers.indexOf(o) % CHART_COLORS.length] }))}
              />
              {stackedOffers.map((o, i) => (
                <Area key={o} type="monotone" dataKey={o} stackId="1"
                  stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]}
                  fillOpacity={1} strokeWidth={0.5} name={o} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Margin Over Time */}
      {revenueData.length > 0 && (() => {
        // Margin is owner net profit, not merely contractual take-home. The
        // selected-period expense feed includes salaries and actual processor
        // fees, so subtract those by business date before calculating margin.
        const expensesByDate = new Map(expensesData.map((day) => [day.date, Number(day.total || 0)]));
        const weeklyMargin: { date: string; margin: number; cash: number; takeHome: number; expenses: number; netProfit: number }[] = [];
        for (let i = 0; i < revenueData.length; i += 7) {
          const chunk = revenueData.slice(i, i + 7);
          const totalCash = chunk.reduce((s, d) => s + (d.cash || 0), 0);
          const totalTH = chunk.reduce((s, d) => s + (d.takeHome || 0), 0);
          const totalExpenses = chunk.reduce((s, d) => s + (expensesByDate.get(d.date) || 0), 0);
          const netProfit = totalTH - totalExpenses;
          weeklyMargin.push({
            date: chunk[0]?.date ?? '',
            margin: Math.round((totalCash > 0 ? (netProfit / totalCash) * 100 : 0) * 10) / 10,
            cash: totalCash,
            takeHome: totalTH,
            expenses: totalExpenses,
            netProfit,
          });
        }
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <h3 className="text-base font-semibold text-foreground mb-1">Margin Over Time</h3>
            <p className="text-xs text-muted-foreground mb-4">Net profit as % of cash collected, after team and processor expenses (weekly)</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={weeklyMargin}>
                <defs>
                  <linearGradient id="marginG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} domain={[0, 'auto']} />
                <Tooltip content={({ active, payload, label }: { active?: boolean; payload?: Array<{ payload?: { margin?: number; cash?: number; takeHome?: number; expenses?: number; netProfit?: number } }>; label?: string }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-lg text-xs">
                      <p className="text-muted-foreground mb-1">{label}</p>
                      <p className="font-bold text-[var(--brand-primary)]">Net margin: {d?.margin}%</p>
                      <p className="text-muted-foreground">Cash: {fmtFull$(d?.cash || 0)}</p>
                      <p className="text-muted-foreground">Owner take-home: {fmtFull$(d?.takeHome || 0)}</p>
                      <p className="text-muted-foreground">Team + processor expenses: {fmtFull$(d?.expenses || 0)}</p>
                      <p className="font-semibold text-foreground">Net profit: {fmtFull$(d?.netProfit || 0)}</p>
                    </div>
                  );
                }} />
                <Area type="monotone" dataKey="margin" stroke="var(--brand-primary)" strokeWidth={2.5} fill="url(#marginG)" name="Margin %" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        );
      })()}

      {/* Expense Tracker */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-card rounded-2xl border border-border p-6"
      >
        <div className="flex justify-between items-center mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Expense Tracker</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Log and manage operational expenses</p>
          </div>
          {showExpenses && (
            <button
              onClick={() => setAddingExpense(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] hover:opacity-90 transition-opacity"
            >
              <Plus size={15} /> Add Expense
            </button>
          )}
        </div>

        {showExpenses && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Date', 'Attributed To', 'Category', 'Description', 'Amount', 'Recurring', 'Source', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground border-b border-border uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr><td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No expenses recorded</td></tr>
                ) : expenses.map(e => (
                  <tr key={e._id} className="border-b border-border hover:bg-secondary/20 transition-colors">
                    <td className="py-3 px-3 text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                    <td className="py-3 px-3 text-xs font-medium text-foreground">{e.offer || 'All Offers'}</td>
                    <td className="py-3 px-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-secondary text-muted-foreground">{e.category}</span>
                    </td>
                    <td className="py-3 px-3 text-sm text-foreground font-medium">{e.description}</td>
                    <td className="py-3 px-3 text-sm font-bold text-red-400">{fmtFull$(e.amount)}</td>
                    <td className="py-3 px-3 text-xs">
                      <span className={e.recurring ? 'text-emerald-400 font-semibold' : 'text-muted-foreground'}>{e.recurring ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">{e.source || 'Manual'}</td>
                    <td className="py-3 px-3">
                      {e.readOnly ? <span className="text-xs text-muted-foreground">Synced</span> : (
                        <div className="flex gap-2">
                          <button onClick={() => setEditingExpense(e)} className="text-muted-foreground hover:text-foreground transition-colors p-1"><Edit2 size={14} /></button>
                          <button onClick={() => setDeletingExpense(e)} className="text-muted-foreground hover:text-red-500 transition-colors p-1"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Dialogs */}
      {selectedMetric && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedMetric(null)}>
          <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/15 bg-background shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-border px-7 py-5">
              <div><h2 className="text-2xl font-bold">{selectedMetric} breakdown</h2><p className="mt-1 text-sm text-muted-foreground">A selected-period accounting view. Every row below reconciles to this card.</p></div>
              <button aria-label="Close" className="rounded-md px-2 py-1 text-xl text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setSelectedMetric(null)}>✕</button>
            </div>
            <div className="overflow-y-auto p-7">
              <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Selected metric</p><p className="mt-1 text-2xl font-bold">{selectedMetric === 'Net Margin' ? `${netMargin.toFixed(1)}%` : selectedMetric === 'Net Profit' ? fmtFull$(netProfit) : selectedMetric === 'Total Expenses' ? fmtFull$(totalExpenses) : selectedMetric === 'Affiliate Payouts' ? fmtFull$(affiliatePayouts) : selectedMetric === 'Cash Collected' ? fmtFull$(cashCollected) : selectedMetric === 'Team Payouts' ? fmtFull$(teamPayouts) : selectedMetric === 'Closer Payouts' ? fmtFull$(closerPayouts) : selectedMetric === 'Setter Payouts' ? fmtFull$(setterPayouts) : fmtFull$(companyTakeHome)}</p></div>
                {selectedMetric !== 'Affiliate Payouts' && <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cash collected</p><p className="mt-1 text-2xl font-bold">{fmtFull$(cashCollected)}</p></div>}
                <div className={`rounded-xl border border-border bg-muted/30 p-4 ${selectedMetric === 'Affiliate Payouts' ? 'md:col-span-2' : ''}`}><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Period formula</p><p className="mt-1 text-sm font-medium">{selectedMetric === 'Affiliate Payouts' ? 'Net affiliate payouts by platform and referred company' : selectedMetric === 'Net Margin' ? 'Net Profit ÷ Cash Collected' : selectedMetric === 'Net Profit' ? 'Combined Take-Home − Total Expenses' : 'See line-item detail below'}</p></div>
              </div>

              {selectedMetric === 'Affiliate Payouts' && <section className="mb-7"><h3 className="mb-1 text-lg font-semibold">Affiliate payout distribution</h3><p className="mb-3 text-sm text-muted-foreground">Net affiliate payouts for the selected period, ranked from the highest-paying company to the lowest. Commas uses referral-type / Referral Commission data where supplied; Whop’s personal ledger does not expose originating-company metadata, so it is shown as Whop partner payout.</p><div className="overflow-hidden rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Platform</th><th className="px-4 py-3">Company / payout source</th><th className="px-4 py-3 text-right">Transactions</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">Fees / reversals</th><th className="px-4 py-3 text-right">Net payout</th><th className="px-4 py-3 text-right">Share</th></tr></thead><tbody>{affiliateDistribution.map((item) => <tr key={`${item.source}-${item.company}`} className="border-t border-border/70"><td className="px-4 py-3 font-medium">{item.source}</td><td className="px-4 py-3">{item.company}</td><td className="px-4 py-3 text-right">{item.count}</td><td className="px-4 py-3 text-right">{fmtFull$(item.gross)}</td><td className="px-4 py-3 text-right">{fmtFull$(item.fees + Math.min(0, item.net - item.gross + item.fees))}</td><td className="px-4 py-3 text-right font-medium">{fmtFull$(item.net)}</td><td className="px-4 py-3 text-right">{affiliateDistributionTotal ? `${((item.net / affiliateDistributionTotal) * 100).toFixed(1)}%` : '—'}</td></tr>)}{affiliateDistribution.length === 0 && <tr className="border-t border-border/70"><td colSpan={7} className="px-4 py-3 text-muted-foreground">No affiliate-payout activity exists for this selected period.</td></tr>}<tr className="border-t-2 border-border bg-muted/30 font-bold"><td colSpan={5} className="px-4 py-3">Net affiliate payouts</td><td className="px-4 py-3 text-right">{fmtFull$(affiliateDistributionTotal)}</td><td className="px-4 py-3 text-right">100.0%</td></tr></tbody></table></div></section>}

              {(selectedMetric === 'Cash Collected' || selectedMetric === 'Net Profit' || selectedMetric === 'Net Margin') && <section className="mb-7"><h3 className="mb-1 text-lg font-semibold">Offer cash collected and take-home</h3><p className="mb-3 text-sm text-muted-foreground">Offer Cash Collected is the actual top-line cash each offer made in the selected period. “How much the offer made me” is your calculated owner take-home from that offer.</p><div className="overflow-hidden rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Offer</th><th className="px-4 py-3 text-right">Offer Cash Collected</th><th className="px-4 py-3 text-right">How much the offer made me</th><th className="px-4 py-3 text-right">Share of Total</th></tr></thead><tbody>{modalCashDistribution.map((item) => <tr key={item.offer} className="border-t border-border/70"><td className="px-4 py-3 font-medium">{item.offer}</td><td className="px-4 py-3 text-right">{fmtFull$(item.cashCollected)}</td><td className="px-4 py-3 text-right font-medium">{fmtFull$(item.ownerTakeHome || 0)}</td><td className="px-4 py-3 text-right">{offerCashCollectedTotal ? `${((item.cashCollected / offerCashCollectedTotal) * 100).toFixed(1)}%` : '—'}</td></tr>)}<tr className="border-t-2 border-border bg-muted/30 font-bold"><td className="px-4 py-3">Total</td><td className="px-4 py-3 text-right">{fmtFull$(offerCashCollectedTotal)}</td><td className="px-4 py-3 text-right">{fmtFull$(takeHome)}</td><td className="px-4 py-3 text-right">100.0%</td></tr></tbody></table></div></section>}

              {(selectedMetric === 'Closer Payouts' || selectedMetric === 'Setter Payouts' || selectedMetric === 'Team Payouts') && <section className="mb-7"><h3 className="mb-1 text-lg font-semibold">{selectedMetric} by offer</h3><p className="mb-3 text-sm text-muted-foreground">This selected-period view shows the payout liability assigned to each offer. Individual-rep detail will appear where a rep-level payout record is present.</p><div className="overflow-hidden rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Offer</th><th className="px-4 py-3 text-right">Closer payout</th><th className="px-4 py-3 text-right">Setter payout</th><th className="px-4 py-3 text-right">Total team payout</th></tr></thead><tbody>{offerPnl.map((item) => { const closer = item.closerCommission || 0; const setter = item.setterPayout || 0; return <tr key={item.offer} className="border-t border-border/70"><td className="px-4 py-3 font-medium">{item.offer}</td><td className="px-4 py-3 text-right">{fmtFull$(closer)}</td><td className="px-4 py-3 text-right">{fmtFull$(setter)}</td><td className="px-4 py-3 text-right font-medium">{fmtFull$(closer + setter)}</td></tr>; })}<tr className="border-t-2 border-border bg-muted/30 font-bold"><td className="px-4 py-3">Total</td><td className="px-4 py-3 text-right">{fmtFull$(closerPayouts)}</td><td className="px-4 py-3 text-right">{fmtFull$(setterPayouts)}</td><td className="px-4 py-3 text-right">{fmtFull$(teamPayouts)}</td></tr></tbody></table></div></section>}

              {(selectedMetric === 'Company Take Home' || selectedMetric === 'Net Profit' || selectedMetric === 'Net Margin') && <section className="mb-7"><h3 className="mb-1 text-lg font-semibold">Combined take-home by offer</h3><p className="mb-3 text-sm text-muted-foreground">Your owner share is calculated from each offer’s contractual billing basis—not from gross cash alone.</p><div className="overflow-hidden rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Offer</th><th className="px-4 py-3">Calculation basis</th><th className="px-4 py-3 text-right">Company revenue</th><th className="px-4 py-3 text-right">Your take-home</th></tr></thead><tbody>{offerPnl.map((item) => <tr key={item.offer} className="border-t border-border/70"><td className="px-4 py-3 font-medium">{item.offer}</td><td className="max-w-sm px-4 py-3 text-xs text-muted-foreground">{item.formula}</td><td className="px-4 py-3 text-right">{item.companyRevenue === undefined ? 'Unavailable' : fmtFull$(item.companyRevenue)}</td><td className="px-4 py-3 text-right font-medium">{fmtFull$(item.ownerTakeHome || 0)}</td></tr>)}<tr className="border-t-2 border-border bg-muted/30 font-bold"><td colSpan={3} className="px-4 py-3">Combined take-home</td><td className="px-4 py-3 text-right">{fmtFull$(takeHome)}</td></tr></tbody></table></div></section>}

              {(selectedMetric === 'Total Expenses' || selectedMetric === 'Net Profit' || selectedMetric === 'Net Margin') && <section className="mb-7"><h3 className="mb-1 text-lg font-semibold">Total expenses</h3><p className="mb-3 text-sm text-muted-foreground">Only salaries paid in the selected period and processor fees from matched Info Scalers client payouts are deducted from personal net profit.</p><div className="overflow-hidden rounded-xl border border-border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Person / source</th><th className="px-4 py-3">Date / offer</th><th className="px-4 py-3 text-right">Amount</th></tr></thead><tbody>{salaryExpenseItems.map((expense) => <tr key={expense._id} className="border-t border-border/70"><td className="px-4 py-3">Salary</td><td className="px-4 py-3 font-medium">{expense.description || 'Unspecified salary'}</td><td className="px-4 py-3 text-muted-foreground">{new Date(expense.date).toLocaleDateString()}</td><td className="px-4 py-3 text-right">{fmtFull$(expense.amount)}</td></tr>)}{salaryExpenseItems.length === 0 && <tr className="border-t border-border/70"><td colSpan={4} className="px-4 py-3 text-muted-foreground">No individual salary records are available for this selected period.</td></tr>}{processorFeeItems.map((item) => <tr key={item.offer} className="border-t border-border/70"><td className="px-4 py-3">Processor fee</td><td className="px-4 py-3 font-medium">Matched client payout</td><td className="px-4 py-3 text-muted-foreground">{item.offer}</td><td className="px-4 py-3 text-right">{fmtFull$(item.processingFees as number)}</td></tr>)}<tr className="border-t-2 border-border bg-muted/30 font-bold"><td colSpan={3} className="px-4 py-3">Total expenses</td><td className="px-4 py-3 text-right">{fmtFull$(totalExpenses)}</td></tr></tbody></table></div></section>}

              {(selectedMetric === 'Net Profit' || selectedMetric === 'Net Margin') && <section><h3 className="mb-3 text-lg font-semibold">Final reconciliation</h3><div className="rounded-xl border border-border bg-muted/20 p-5 text-sm"><div className="flex justify-between py-1"><span>Combined take-home</span><strong>{fmtFull$(takeHome)}</strong></div><div className="flex justify-between py-1"><span>Less total expenses</span><strong>−{fmtFull$(totalExpenses)}</strong></div><div className="mt-2 flex justify-between border-t border-border pt-3 text-base"><span className="font-bold">Net profit</span><strong>{fmtFull$(netProfit)}</strong></div>{selectedMetric === 'Net Margin' && <><div className="mt-4 flex justify-between py-1"><span>Net profit ÷ cash collected</span><strong>{fmtFull$(netProfit)} ÷ {fmtFull$(cashCollected)}</strong></div><div className="flex justify-between pt-2 text-base"><span className="font-bold">Net margin</span><strong>{netMargin.toFixed(1)}%</strong></div></>}</div></section>}
            </div>
          </div>
        </div>
      )}
      <PayoutConfigDialog open={configDialogMode !== null} onClose={() => { setConfigDialogMode(null); fetchSummary(); }} mode={configDialogMode || 'takeHome'} />
      <OtherOffersPopup open={otherOffersOpen} onClose={() => setOtherOffersOpen(false)} offers={incomeByOffer} />
      <EditRowDialog open={addingExpense} onOpenChange={setAddingExpense} title="Add Expense" description="Add a new expense to track your costs." fields={expenseFields} data={addingExpense ? getNewExpenseData() : null} onSave={handleAddExpense} />
      <EditRowDialog open={!!editingExpense} onOpenChange={(open) => { if (!open) setEditingExpense(null); }} title="Edit Expense" description="Update expense details." fields={expenseFields} data={editingExpense ? getEditData(editingExpense) : null} onSave={handleEditExpense} />
      <DeleteConfirmDialog open={!!deletingExpense} onOpenChange={(open) => { if (!open) setDeletingExpense(null); }} title="Delete Expense" description="Are you sure you want to delete this expense?" itemName={deletingExpense?.description} onConfirm={handleDeleteExpense} />
    </div>
  );
}

export default Financials;
